import React, { useState, useMemo } from 'react';
import { Search, MoreVertical, Archive, Trash2, ChevronDown, ChevronRight, CheckSquare, Square, Ban } from 'lucide-react';
import { Conversation, ChannelType, CHANNEL_INFO } from '../../types/conversation';
import { flooringBotDb } from '../../lib/supabase';
import ChannelBadge from './ChannelBadge';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onConversationUpdate: () => void;
  layoutMode?: 'dropdown' | 'tabs';
  onBlockSender?: (email: string, name?: string) => void;
}

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  onConversationUpdate,
  layoutMode = 'dropdown',
  onBlockSender,
}: ConversationListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [contextMenu, setContextMenu] = useState<string | null>(null);
  const [collapsedChannels, setCollapsedChannels] = useState<Set<ChannelType>>(new Set());
  const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);

  // Group conversations by channel
  const conversationsByChannel = useMemo(() => {
    const filtered = conversations.filter((conv) =>
      conv.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const grouped = new Map<ChannelType, Conversation[]>();

    filtered.forEach((conv) => {
      const channel = conv.channel || 'sms';
      if (!grouped.has(channel)) {
        grouped.set(channel, []);
      }
      grouped.get(channel)!.push(conv);
    });

    return grouped;
  }, [conversations, searchTerm]);

  const toggleChannel = (channel: ChannelType) => {
    setCollapsedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  const formatTimestamp = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleContextMenu = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setContextMenu(contextMenu === id ? null : id);
  };

  const handleArchive = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setContextMenu(null);

    const success = await flooringBotDb.archiveConversation(conversationId);
    if (success) {
      onConversationUpdate();
    }
  };

  const handleDelete = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setContextMenu(null);

    if (confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      const success = await flooringBotDb.deleteConversation(conversationId);
      if (success) {
        onConversationUpdate();
      }
    }
  };

  const toggleConversationSelection = (conversationId: string) => {
    setSelectedConversations(prev => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedConversations.size === conversations.length) {
      setSelectedConversations(new Set());
    } else {
      setSelectedConversations(new Set(conversations.map(c => c.id)));
    }
  };

  const handleBulkArchive = async () => {
    if (selectedConversations.size === 0) return;

    const confirmed = confirm(`Archive ${selectedConversations.size} conversation(s)?`);
    if (!confirmed) return;

    for (const id of selectedConversations) {
      await flooringBotDb.archiveConversation(id);
    }

    setSelectedConversations(new Set());
    setBulkActionMode(false);
    onConversationUpdate();
  };

  const handleBlockSender = (e: React.MouseEvent, conversation: any) => {
    e.stopPropagation();
    setContextMenu(null);

    const senderEmail = conversation.participants?.[0]?.email || conversation.participants?.[0]?.gmail || '';
    const senderName = conversation.participants?.[0]?.name || conversation.title || '';

    if (!senderEmail) {
      alert('No email address found for this sender');
      return;
    }

    if (onBlockSender) {
      onBlockSender(senderEmail, senderName);
      onConversationUpdate();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#161b22] border-r border-[#30363d]">
      {/* Search Bar & Bulk Actions */}
      <div className="p-4 border-b border-[#30363d]">
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                       text-gray-300 placeholder-gray-500 text-sm
                       focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500
                       transition-all"
            />
          </div>
          <button
            onClick={() => {
              setBulkActionMode(!bulkActionMode);
              setSelectedConversations(new Set());
            }}
            className={`p-2 rounded-lg transition-colors ${
              bulkActionMode
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
            }`}
            title="Select multiple"
          >
            <CheckSquare className="w-4 h-4" />
          </button>
        </div>

        {/* Bulk Actions Bar */}
        {bulkActionMode && (
          <div className="flex items-center justify-between p-2 bg-[#1c2128] border border-[#30363d] rounded-lg">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
            >
              {selectedConversations.size === conversations.length ? (
                <CheckSquare className="w-4 h-4 text-cyan-400" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              <span>
                {selectedConversations.size === conversations.length
                  ? 'Deselect All'
                  : `Select All (${conversations.length})`}
              </span>
            </button>

            {selectedConversations.size > 0 && (
              <button
                onClick={handleBulkArchive}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-400
                         border border-purple-500/30 rounded-lg text-sm font-medium
                         hover:bg-purple-500/30 transition-colors"
              >
                <Archive className="w-3.5 h-3.5" />
                Archive ({selectedConversations.size})
              </button>
            )}
          </div>
        )}
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {layoutMode === 'tabs' && conversations.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
            {searchTerm ? 'No conversations found' : 'No conversations yet'}
          </div>
        ) : layoutMode === 'dropdown' && conversationsByChannel.size === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
            {searchTerm ? 'No conversations found' : 'No conversations yet'}
          </div>
        ) : layoutMode === 'tabs' ? (
          /* Tab Mode - Flat List */
          <div className="space-y-1 p-2">
            {conversations.filter((conv) =>
              conv.title.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => !bulkActionMode && onSelect(conversation.id)}
                className={`
                  relative group rounded-lg p-3 cursor-pointer transition-all
                  ${selectedId === conversation.id
                    ? 'bg-[#1c2128] border-l-2 border-cyan-500 shadow-lg shadow-cyan-500/10'
                    : 'bg-[#0d1117] hover:bg-[#1c2128] border-l-2 border-transparent'
                  }
                `}
              >
                <div className="flex items-start gap-2">
                  {/* Checkbox in bulk mode */}
                  {bulkActionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleConversationSelection(conversation.id);
                      }}
                      className="mt-0.5 flex-shrink-0"
                    >
                      {selectedConversations.has(conversation.id) ? (
                        <CheckSquare className="w-4 h-4 text-cyan-400" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  )}

                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <ChannelBadge channel={conversation.channel || 'sms'} size="sm" />
                        <h3 className="font-semibold text-sm text-gray-200 truncate">
                          {conversation.title}
                        </h3>
                      </div>
                      <span className="text-xs text-gray-500 shrink-0">
                        {formatTimestamp(conversation.lastMessageAt || new Date())}
                      </span>
                    </div>

                    <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                      {conversation.lastMessage || 'No messages yet'}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        {conversation.unread && (
                          <span className="flex items-center gap-1 text-xs text-cyan-400">
                            <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                            New
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  {!bulkActionMode && (
                    <div className="relative">
                      <button
                        onClick={(e) => handleContextMenu(e, conversation.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#30363d]
                                 rounded transition-opacity"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>

                      {contextMenu === conversation.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-[#1c2128]
                                      border border-[#30363d] rounded-lg shadow-xl z-50
                                      animate-in fade-in slide-in-from-top-1 duration-150">
                          <button
                            onClick={(e) => handleArchive(e, conversation.id)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300
                                     hover:bg-[#30363d] flex items-center gap-2
                                     rounded-t-lg transition-colors">
                            <Archive className="w-3 h-3" />
                            Archive
                          </button>
                          {conversation.channel === 'gmail' && onBlockSender && (
                            <button
                              onClick={(e) => handleBlockSender(e, conversation)}
                              className="w-full px-3 py-2 text-left text-sm text-orange-400
                                       hover:bg-[#30363d] flex items-center gap-2
                                       transition-colors">
                              <Ban className="w-3 h-3" />
                              Block Sender
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(e, conversation.id)}
                            className="w-full px-3 py-2 text-left text-sm text-red-400
                                     hover:bg-[#30363d] flex items-center gap-2
                                     rounded-b-lg transition-colors">
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Dropdown Mode - Grouped by Channel */
          <div className="space-y-3 p-2">
            {Array.from(conversationsByChannel.entries()).map(([channel, channelConversations]) => {
              const channelInfo = CHANNEL_INFO[channel];
              const isCollapsed = collapsedChannels.has(channel);

              return (
                <div key={channel} className="space-y-1">
                  {/* Channel Header */}
                  <button
                    onClick={() => toggleChannel(channel)}
                    className="w-full flex items-center justify-between px-2 py-1.5
                             hover:bg-[#1c2128] rounded transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                      <ChannelBadge channel={channel} size="sm" showLabel />
                      <span className="text-xs text-gray-500 font-medium">
                        ({channelConversations.length})
                      </span>
                    </div>
                  </button>

                  {/* Conversations in Channel */}
                  {!isCollapsed && (
                    <div className="space-y-1 ml-2">
                      {channelConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => !bulkActionMode && onSelect(conversation.id)}
                className={`
                  relative group rounded-lg p-3 cursor-pointer transition-all
                  ${selectedId === conversation.id
                    ? 'bg-[#1c2128] border-l-2 border-cyan-500 shadow-lg shadow-cyan-500/10'
                    : 'bg-[#0d1117] hover:bg-[#1c2128] border-l-2 border-transparent'
                  }
                `}
              >
                <div className="flex items-start gap-2">
                  {/* Checkbox in bulk mode */}
                  {bulkActionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleConversationSelection(conversation.id);
                      }}
                      className="mt-0.5 flex-shrink-0"
                    >
                      {selectedConversations.has(conversation.id) ? (
                        <CheckSquare className="w-4 h-4 text-cyan-400" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  )}

                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                    {/* Title, Channel Badge & Timestamp */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <ChannelBadge channel={conversation.channel || 'sms'} size="sm" />
                        <h3 className="font-semibold text-sm text-gray-200 truncate">
                          {conversation.title}
                        </h3>
                      </div>
                      <span className="text-xs text-gray-500 shrink-0">
                        {formatTimestamp(conversation.lastMessageAt || new Date())}
                      </span>
                    </div>

                    {/* Preview */}
                    <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                      {conversation.lastMessage || 'No messages yet'}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        {conversation.unread && (
                          <span className="flex items-center gap-1 text-xs text-cyan-400">
                            <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                            New
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  {!bulkActionMode && (
                    <div className="relative">
                      <button
                        onClick={(e) => handleContextMenu(e, conversation.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#30363d]
                                 rounded transition-opacity"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>

                      {contextMenu === conversation.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-[#1c2128]
                                      border border-[#30363d] rounded-lg shadow-xl z-50
                                      animate-in fade-in slide-in-from-top-1 duration-150">
                          <button
                            onClick={(e) => handleArchive(e, conversation.id)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300
                                     hover:bg-[#30363d] flex items-center gap-2
                                     rounded-t-lg transition-colors">
                            <Archive className="w-3 h-3" />
                            Archive
                          </button>
                          {conversation.channel === 'gmail' && onBlockSender && (
                            <button
                              onClick={(e) => handleBlockSender(e, conversation)}
                              className="w-full px-3 py-2 text-left text-sm text-orange-400
                                       hover:bg-[#30363d] flex items-center gap-2
                                       transition-colors">
                              <Ban className="w-3 h-3" />
                              Block Sender
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(e, conversation.id)}
                            className="w-full px-3 py-2 text-left text-sm text-red-400
                                     hover:bg-[#30363d] flex items-center gap-2
                                     rounded-b-lg transition-colors">
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-[#30363d] bg-[#0d1117]">
        <div className="text-xs text-gray-500">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}
